import type * as React from 'react'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import type { FieldBinding } from './types'

interface InputFieldProps<T extends object>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'value' | 'form'>,
    FieldBinding<T> {
  label?: React.ReactNode
  hint?: React.ReactNode
  required?: boolean
  labelExtra?: React.ReactNode
  /** Sits inside the control, right-aligned: a reveal toggle, a unit, a copy. */
  trailing?: React.ReactNode
  className?: string
}

export function InputField<T extends object>({
  form,
  name,
  label,
  hint,
  required,
  labelExtra,
  trailing,
  className,
  ...rest
}: InputFieldProps<T>) {
  const error = form.errors[name]

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={name}
      labelExtra={labelExtra}
      className={className}
    >
      <div className={trailing ? 'relative' : undefined}>
        <Input
          {...rest}
          id={name}
          value={String(form.data[name] ?? '')}
          onChange={(event) => form.setData(name, event.target.value as T[keyof T & string])}
          autoComplete={rest.autoComplete ?? 'off'}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : hint ? `${name}-hint` : undefined}
          className={trailing ? 'pr-10' : undefined}
        />
        {trailing && (
          <span className="absolute inset-y-0 right-0 flex w-10 items-center justify-center">
            {trailing}
          </span>
        )}
      </div>
    </Field>
  )
}
